import Vue from 'vue'

/**
 * Form validation helper — Vue 2 equivalent of useFormValidation.ts
 * Returns a reactive state object instead of refs.
 */
export function useFormValidation() {
  const state = Vue.observable({
    isLoading: false,
    apiValidationErrors: {},
    apiGlobalErrors: [],
    apiRawError: null,
  })

  function collectBackendErrors(node, accMsgs, accFields) {
    if (!accMsgs) accMsgs = []
    if (!accFields) accFields = {}
    if (!node || typeof node !== 'object') return { accMsgs, accFields }

    function addMsg(msg) {
      if (msg && typeof msg === 'string' && !accMsgs.includes(msg)) {
        accMsgs.push(msg)
      }
    }

    addMsg(node.message)
    addMsg(node.error)

    if (node.errors) {
      if (Array.isArray(node.errors)) {
        node.errors.forEach(function (err) {
          var txt = (err && typeof err === 'object' && (err.detail || err.title)) || err
          if (typeof txt === 'string') addMsg(txt)
          var pointer = err && err.source && err.source.pointer && err.source.pointer.split('/').pop()
          if (pointer && typeof txt === 'string') {
            accFields[pointer] = (accFields[pointer] || []).concat(txt)
          }
        })
      } else if (typeof node.errors === 'object') {
        Object.keys(node.errors).forEach(function (field) {
          var msgs = node.errors[field]
          var arr = Array.isArray(msgs) ? msgs : [msgs]
          var filtered = arr.filter(function (m) { return typeof m === 'string' && m.length > 0 })
          if (filtered.length) {
            accFields[field] = (accFields[field] || []).concat(filtered)
            addMsg(filtered[0])
          }
        })
      }
    }

    if (node.response) {
      collectBackendErrors(node.response, accMsgs, accFields)
    }

    return { accMsgs: accMsgs, accFields: accFields }
  }

  function extractMessages(errors) {
    if (!errors) return []
    if (Array.isArray(errors)) return errors.filter(Boolean)
    if (typeof errors === 'object') {
      return Object.values(errors).reduce(function (acc, val) {
        return acc.concat(Array.isArray(val) ? val : [val])
      }, []).filter(Boolean)
    }
    return [errors].filter(Boolean)
  }

  function scrollToFirstError(container) {
    Vue.nextTick(function () {
      var root = container || document
      var errorEl = root.querySelector('.ec-field-error, .is-invalid')
      if (errorEl) {
        var parent = errorEl.closest('div')
        if (parent) parent.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    })
  }

  function setApiValidation(serverErrors, container) {
    if (!serverErrors) return {}

    if (serverErrors.attachment) {
      Vue.set(state, 'apiValidationErrors', { attachment: [serverErrors.attachment[0]] })
      state.apiGlobalErrors = [serverErrors.attachment[0]].filter(Boolean)
      return { attachment: serverErrors.attachment[0] }
    }

    if (!Array.isArray(serverErrors) && typeof serverErrors === 'object') {
      Vue.set(state, 'apiValidationErrors', serverErrors)
      state.apiGlobalErrors = extractMessages(serverErrors)
    } else {
      var fieldErrors = serverErrors.reduce(function (acc, err) {
        if (!err.source) return acc
        var field = err.source.pointer.split('/')[3]
        acc[field] = (acc[field] || []).concat(err.detail)
        return acc
      }, {})
      Vue.set(state, 'apiValidationErrors', fieldErrors)
      state.apiGlobalErrors = extractMessages(fieldErrors)
    }

    scrollToFirstError(container)
    return {}
  }

  function resetApiValidation() {
    Vue.set(state, 'apiValidationErrors', {})
    state.apiGlobalErrors = []
    state.apiRawError = null
  }

  function handleBackendFormError(error, container) {
    var payload = (error && error.response && error.response.data) || error || {}
    var result = collectBackendErrors(payload)

    state.apiRawError = payload

    if (Object.keys(result.accFields).length) {
      setApiValidation(result.accFields, container)
    } else {
      Vue.set(state, 'apiValidationErrors', {})
    }

    state.apiGlobalErrors = result.accMsgs.filter(Boolean)

    return {
      messages: state.apiGlobalErrors,
      fieldErrors: state.apiValidationErrors,
      rawError: state.apiRawError,
    }
  }

  function getFieldError(field) {
    var errors = state.apiValidationErrors[field]
    return errors && errors.length ? errors[0] : undefined
  }

  function hasFieldError(field) {
    return !!(state.apiValidationErrors[field] && state.apiValidationErrors[field].length)
  }

  return {
    // Expose reactive state directly (use as this.validation.isLoading etc.)
    get isLoading() { return state.isLoading },
    set isLoading(v) { state.isLoading = v },
    get apiValidationErrors() { return state.apiValidationErrors },
    get apiGlobalErrors() { return state.apiGlobalErrors },
    get apiRawError() { return state.apiRawError },
    collectBackendErrors: collectBackendErrors,
    extractMessages: extractMessages,
    setApiValidation: setApiValidation,
    resetApiValidation: resetApiValidation,
    handleBackendFormError: handleBackendFormError,
    scrollToFirstError: scrollToFirstError,
    getFieldError: getFieldError,
    hasFieldError: hasFieldError,
  }
}
